package com.dualremotetest.web

import com.dualremotetest.AppState
import com.dualremotetest.DeviceInfo
import com.dualremotetest.Dir
import com.dualremotetest.Devices
import com.dualremotetest.Difficulty
import com.dualremotetest.FIRE_KEYS
import com.dualremotetest.KeyInput
import com.dualremotetest.Keys
import com.dualremotetest.MoveMode
import com.dualremotetest.OnlinePlay
import com.dualremotetest.PadButton
import com.dualremotetest.PadSticks
import com.dualremotetest.Phase
import com.dualremotetest.PixelRenderer
import com.dualremotetest.TankMatch
import com.dualremotetest.currentTimeMillis
import com.dualremotetest.keyLabel
import com.dualremotetest.nanoTime
import com.dualremotetest.padButton
import com.dualremotetest.pictureScale
import com.dualremotetest.touchKey
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.khronos.webgl.Int32Array
import org.khronos.webgl.set
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

// The browser host for the shared game, the counterpart of TankGameActivity + TankUi on Android: one canvas shows the
// 256×224 picture and its side panels at the largest whole-number scale, and every input – keyboard, the on-screen
// controls, game controllers – arrives at the game as the same key events a TV remote sends.

private val diag = AppState.gameDiagnostics
private val match = AppState.tankMatch
private val renderer = PixelRenderer()
/** Online play through the relay (server/ in the repository), whose address the build puts in [RELAY_URL]. */
private val online = OnlinePlay(match, RELAY_URL, ::WebSocketNetLink, ::uptime).also { AppState.online = it }

/** The keyboard's input channel (real Android devices have ids ≥ 0; the touch controls use TOUCH_DEVICE_ID). */
private const val KEYBOARD_ID = 1
/** Game controller n (navigator.getGamepads() index) is device GAMEPAD_ID_BASE + n. */
private const val GAMEPAD_ID_BASE = 10

private val touch: Boolean = js("(navigator.maxTouchPoints || 0) > 0 || ('ontouchstart' in window)") as Boolean

private lateinit var game: HTMLCanvasElement
private lateinit var ctx: CanvasRenderingContext2D
private var pixels: dynamic = null // ImageData
private lateinit var pixelInts: Int32Array
private lateinit var audio: WebTankAudio
private var controls: TouchControls? = null
private val sticks = PadSticks()
private val keyRepeats = HashMap<Int, Int>()
private val padButtons = HashMap<Int, BooleanArray>()

fun main() {
    game = document.getElementById("game") as HTMLCanvasElement
    game.width = PixelRenderer.FULL_W
    game.height = PixelRenderer.H
    ctx = game.getContext("2d") as CanvasRenderingContext2D
    pixels = ctx.createImageData(PixelRenderer.FULL_W.toDouble(), PixelRenderer.H.toDouble())
    pixelInts = Int32Array(pixels.data.buffer as org.khronos.webgl.ArrayBuffer)

    // One player per screen on phones and tablets (2 PLAYERS is for the TV with two remotes), and a web page cannot
    // close itself, so the title has no EXIT.
    match.touchDevice = true
    match.canExit = false
    diag.environment = environment()
    match.input.deviceLookup = { id -> devices().firstOrNull { it.id == id } }
    audio = WebTankAudio()
    match.audio = audio
    loadSettings()
    match.onSettingsChanged = ::saveSettings
    match.onSaveReport = ::saveReport
    refreshDevices()
    match.openTitle()
    if (audio.locked) match.showPressStart()

    if (touch) controls = TouchControls(document.getElementById("touch") as HTMLCanvasElement, match, ::onTouchKey, ::onPictureTap)
    else {
        // A computer: the menus name the keys, and the mouse can pick menu rows too.
        match.keyboardHints = true
        game.addEventListener("pointermove", { onMenuPointer(it, click = false) })
        game.addEventListener("pointerdown", { onMenuPointer(it, click = true) })
    }
    window.addEventListener("resize", { layout() })
    window.addEventListener("orientationchange", { layout() })
    layout()

    window.addEventListener("keydown", { onKeyboard(it as KeyboardEvent, down = true) })
    window.addEventListener("keyup", { onKeyboard(it as KeyboardEvent, down = false) })
    window.addEventListener("blur", { onFocusLost() })
    document.addEventListener("visibilitychange", {
        val hidden = document.asDynamic().hidden as Boolean
        if (hidden) {
            match.pause("page hidden")
            onFocusLost()
        }
        audio.setAppPaused(hidden)
    })
    // Closing or leaving the page ends an online game for the other player too, as closing the app does on Android.
    window.addEventListener("pagehide", { online.session?.quit() })
    // A room code pasted (Ctrl+V / Cmd+V) in the online lobby fills JOIN ROOM.
    window.addEventListener("paste", { e ->
        val text = e.asDynamic().clipboardData?.getData("text") as String?
        if (text != null) online.pasteCode(text)
    })
    window.addEventListener("gamepadconnected", { refreshDevices() })
    window.addEventListener("gamepaddisconnected", { refreshDevices() })
    // Browsers only let a page start sound from inside a touch, click or key press; the first one also ends PRESS START
    // (Esc does not count as one, so it leaves the screen up).
    for (type in listOf("pointerdown", "touchend", "keydown")) window.addEventListener(type, {
        audio.unlock()
        if (type != "keydown" || (it as KeyboardEvent).key != "Escape") match.dismissPressStart()
    }, true)
    registerServiceWorker()

    window.requestAnimationFrame(::frame)
}

private fun frame(@Suppress("UNUSED_PARAMETER") timestamp: Double) {
    pollGamepads()
    if (match.pressStart && !audio.locked) match.dismissPressStart() // this browser lets the page play sound already
    online.frame(nanoTime())
    renderer.render(match)
    val src = renderer.screen
    for (i in src.indices) {
        val c = src[i]
        // ARGB → the canvas's RGBA bytes, read as a little-endian int: ABGR.
        pixelInts[i] = (c and 0xFF00FF00.toInt()) or ((c shr 16) and 0xFF) or ((c and 0xFF) shl 16) or (0xFF shl 24)
    }
    ctx.putImageData(pixels, 0.0, 0.0)
    controls?.draw()
    DiagnosticsOverlay.update(match, audio)
    window.requestAnimationFrame(::frame)
}

/** The largest whole-number scale in device pixels, leaving room for the touch controls, as on Android. */
private fun layout() {
    val dpr = window.devicePixelRatio
    val w = window.innerWidth
    val h = window.innerHeight
    val reserve = if (touch) (TouchControls.RESERVE_CSS_PX * dpr).toInt() else 0
    val scale = pictureScale((w * dpr).toInt(), (h * dpr).toInt(), reserve)
    game.style.width = "${PixelRenderer.FULL_W * scale / dpr}px"
    game.style.height = "${PixelRenderer.H * scale / dpr}px"
    controls?.resize(w, h, dpr, PixelRenderer.W * scale / dpr)
}

// ------------------------------------------------------------------ input

/** Every key from every source, as TankGameActivity.handleKey: BACK (Esc) acts on its UP; the game gets the rest. */
private fun handleKey(input: KeyInput, deviceName: String): Boolean {
    val phaseBefore = match.phase
    val pad = padButton(input.keyCode, phaseBefore)
    if (online.session != null) {
        // Online: every key of this device goes to the session, which hands it to both games in step.
        val back = input.keyCode == Keys.KEYCODE_BACK || input.keyCode == Keys.KEYCODE_ESCAPE || pad == PadButton.BACK
        val code = if (back) Keys.KEYCODE_BACK else input.keyCode
        val used = pad != PadButton.IGNORE && input.repeatCount == 0 &&
            (back || code in FIRE_KEYS || Dir.ofKey(code) != null) && online.sessionKey(code, input.isDown)
        diag.recordKey(input, deviceName, true, if (used) "sent to the online game" else "ignored (online game)", "TANK_${phaseBefore.name}")
        return true
    }
    val consumed: Boolean
    val disposition: String
    when {
        input.keyCode == Keys.KEYCODE_BACK || input.keyCode == Keys.KEYCODE_ESCAPE || pad == PadButton.BACK -> {
            disposition = if (input.isUp && !input.isCanceled) "BACK → ${match.back()}" else "BACK (acts on UP)"
            consumed = true
        }
        pad == PadButton.IGNORE -> {
            disposition = "controller B ignored during play"
            consumed = true
        }
        match.onKey(input) -> {
            consumed = true
            disposition = "consumed by game (${phaseBefore.name.lowercase()})"
        }
        else -> {
            consumed = false
            disposition = "unhandled"
        }
    }
    diag.recordKey(input, deviceName, consumed, disposition, "TANK_${phaseBefore.name}")
    return consumed
}

private fun onTouchKey(keyCode: Int, down: Boolean) {
    handleKey(touchKey(keyCode, down, uptime(), currentTimeMillis()), "on-screen controls")
}

/**
 * Arrows, ESDF or IJKL steer (E/I up, S/J left, D/K down, F/L right: under the left and the right hand's home row);
 * Enter or Space fire and select; Esc or Backspace is BACK. Physical keys, so other keyboard layouts steer the same.
 */
private fun keyboardCode(e: KeyboardEvent): Int? = when (e.code) {
    "ArrowUp", "KeyE", "KeyI" -> Keys.KEYCODE_DPAD_UP
    "ArrowDown", "KeyD", "KeyK" -> Keys.KEYCODE_DPAD_DOWN
    "ArrowLeft", "KeyS", "KeyJ" -> Keys.KEYCODE_DPAD_LEFT
    "ArrowRight", "KeyF", "KeyL" -> Keys.KEYCODE_DPAD_RIGHT
    "Enter" -> Keys.KEYCODE_ENTER
    "NumpadEnter" -> Keys.KEYCODE_NUMPAD_ENTER
    "Space" -> Keys.KEYCODE_SPACE
    "Escape" -> Keys.KEYCODE_ESCAPE
    "Backspace" -> Keys.KEYCODE_BACK
    else -> null
}

private fun onKeyboard(e: KeyboardEvent, down: Boolean) {
    // Shortcuts (Ctrl, Alt, Cmd) are left to the browser, but every release counts, so no direction stays held.
    if (down && (e.ctrlKey || e.metaKey || e.altKey)) return
    if (online.typingCode && typeIntoRoomCode(e, down)) return
    val code = keyboardCode(e) ?: return
    e.preventDefault() // no page scrolling or Backspace navigation
    val repeat = if (down && e.repeat) (keyRepeats[code] ?: 0) + 1 else 0
    if (down) keyRepeats[code] = repeat else keyRepeats.remove(code)
    val now = uptime()
    val input = KeyInput(
        keyCode = code,
        keyName = keyLabel(code).let { if (it.startsWith("key ")) e.code.uppercase() else it },
        action = if (down) Keys.ACTION_DOWN else Keys.ACTION_UP,
        repeatCount = repeat,
        deviceId = KEYBOARD_ID,
        source = Devices.SOURCE_KEYBOARD,
        flags = 0,
        scanCode = 0,
        eventTime = now,
        downTime = now,
        receivedUptime = now,
        wallTime = currentTimeMillis(),
    )
    handleKey(input, "Keyboard")
}

/** JOIN ROOM: letter keys type the room code (ESDF and IJKL included) and Backspace steps back. */
private fun typeIntoRoomCode(e: KeyboardEvent, down: Boolean): Boolean {
    val letter = e.code.removePrefix("Key").takeIf { e.code.startsWith("Key") && it.length == 1 }?.first()
    if (letter == null && e.code != "Backspace") return false
    e.preventDefault()
    if (down) {
        if (letter != null) online.typeLetter(letter) else online.eraseLetter()
    }
    return true
}

/**
 * The mouse over a menu: pointing at a row selects it, and a click acts on it like Enter on the keyboard (so on the
 * title the keyboard becomes Player 1, as it would with Enter).
 */
private fun onMenuPointer(e: Event, click: Boolean) {
    val ev = e.asDynamic()
    if (click && ev.button != 0) return
    if (online.session != null) {
        // Online the menus follow keys only: a row picked by this mouse alone would split the two games.
        game.style.cursor = ""
        return
    }
    val (x, y) = pictureAt(ev.clientX as Double, ev.clientY as Double) ?: return
    val code = online.codeAt(x, y)
    game.style.cursor = if (code != null) "pointer" else ""
    if (code != null) {
        if (click) {
            e.preventDefault()
            copyRoomCode(code)
        }
        return
    }
    val row = PixelRenderer.menuRowAt(match, x, y) ?: return
    match.pointAtMenuRow(row)
    if (!click) return
    e.preventDefault()
    for (down in listOf(true, false)) {
        val now = uptime()
        handleKey(
            KeyInput(
                keyCode = Keys.KEYCODE_ENTER, keyName = "CLICK", action = if (down) Keys.ACTION_DOWN else Keys.ACTION_UP,
                repeatCount = 0, deviceId = KEYBOARD_ID, source = Devices.SOURCE_KEYBOARD, flags = 0, scanCode = 0,
                eventTime = now, downTime = now, receivedUptime = now, wallTime = currentTimeMillis(),
            ),
            "Mouse",
        )
    }
}

/** A point on the page (CSS px) → the picture's pixel (0..256 × 0..224, side panels excluded), or null. */
private fun pictureAt(clientX: Double, clientY: Double): Pair<Int, Int>? {
    val rect = game.getBoundingClientRect()
    if (rect.width <= 0.0 || rect.height <= 0.0) return null
    val x = ((clientX - rect.left) / rect.width * PixelRenderer.FULL_W).toInt() - PixelRenderer.SIDE
    val y = ((clientY - rect.top) / rect.height * PixelRenderer.H).toInt()
    return x to y
}

/** A finger lifted on the picture: tapping the room code copies it. */
private fun onPictureTap(clientX: Double, clientY: Double) {
    val (x, y) = pictureAt(clientX, clientY) ?: return
    online.codeAt(x, y)?.let(::copyRoomCode)
}

/**
 * Puts the room code on the clipboard, then shows CODE COPIED!. Uses the Clipboard API where the page may (https), and
 * the older copy command otherwise (for example inside the iPhone app, whose page is not served over https).
 */
private fun copyRoomCode(code: String) {
    val clipboard = window.navigator.asDynamic().clipboard
    val secure = window.asDynamic().isSecureContext == true
    if (secure && clipboard != null && clipboard.writeText != null) {
        (clipboard.writeText(code) as kotlin.js.Promise<Any?>).then(
            { online.codeCopied() },
            { if (legacyCopy(code)) online.codeCopied() },
        )
    } else if (legacyCopy(code)) {
        online.codeCopied()
    }
}

private fun legacyCopy(text: String): Boolean {
    val area = document.createElement("textarea").asDynamic()
    area.value = text
    area.setAttribute("readonly", "")
    area.style.cssText = "position:fixed;left:-1000px;top:0;opacity:0;-webkit-user-select:text;user-select:text"
    document.body?.appendChild(area as org.w3c.dom.Node)
    area.focus()
    area.select()
    area.setSelectionRange(0, text.length)
    val ok = runCatching { document.asDynamic().execCommand("copy") == true }.getOrDefault(false)
    document.body?.removeChild(area as org.w3c.dom.Node)
    return ok
}

private fun onFocusLost() {
    keyRepeats.clear()
    sticks.reset()
    padButtons.clear()
    controls?.releaseAll()
    online.onLocalFocusLost()
    match.onFocusLost()
}

/** Standard-mapping buttons → Android key codes (A B X Y, shoulders, triggers, SELECT, START, stick clicks, MODE). */
private val PAD_KEYS = mapOf(
    0 to Keys.KEYCODE_BUTTON_A, 1 to Keys.KEYCODE_BUTTON_B, 2 to Keys.KEYCODE_BUTTON_X, 3 to Keys.KEYCODE_BUTTON_Y,
    4 to Keys.KEYCODE_BUTTON_L1, 5 to Keys.KEYCODE_BUTTON_R1, 6 to Keys.KEYCODE_BUTTON_L2, 7 to Keys.KEYCODE_BUTTON_R2,
    8 to Keys.KEYCODE_BUTTON_SELECT, 9 to Keys.KEYCODE_BUTTON_START, 10 to Keys.KEYCODE_BUTTON_THUMBL,
    11 to Keys.KEYCODE_BUTTON_THUMBR, 16 to Keys.KEYCODE_BUTTON_MODE,
)

/** Controllers are polled once a frame: button changes become key presses, the D-pad and left stick go through PadSticks. */
private fun pollGamepads() {
    val pads = gamepads()
    for (i in 0 until pads.length as Int) {
        val pad = pads[i] ?: continue
        if (!(pad.connected as Boolean)) continue
        val id = GAMEPAD_ID_BASE + (pad.index as Int)
        val name = pad.id as String
        val buttons = pad.buttons
        val count = buttons.length as Int
        val was = padButtons.getOrPut(id) { BooleanArray(17) }
        fun pressed(b: Int) = b < count && (buttons[b].pressed as Boolean)
        val now = uptime()
        val wall = currentTimeMillis()
        for ((b, code) in PAD_KEYS) {
            val p = pressed(b)
            if (p == was[b]) continue
            was[b] = p
            handleKey(padKey(code, p, id, now, wall), name)
        }
        val hatX = (if (pressed(15)) 1f else 0f) - (if (pressed(14)) 1f else 0f)
        val hatY = (if (pressed(13)) 1f else 0f) - (if (pressed(12)) 1f else 0f)
        val axes = pad.axes
        val x = if ((axes.length as Int) > 0) (axes[0] as Double).toFloat() else 0f
        val y = if ((axes.length as Int) > 1) (axes[1] as Double).toFloat() else 0f
        sticks.update(id, Devices.SOURCE_JOYSTICK, x, y, hatX, hatY, now, wall).forEach { handleKey(it, name) }
    }
}

private fun padKey(code: Int, down: Boolean, deviceId: Int, now: Long, wall: Long) = KeyInput(
    keyCode = code,
    keyName = keyLabel(code).let { if (it.startsWith("key ")) "BUTTON $code" else it },
    action = if (down) Keys.ACTION_DOWN else Keys.ACTION_UP,
    repeatCount = 0,
    deviceId = deviceId,
    source = Devices.SOURCE_GAMEPAD,
    flags = 0,
    scanCode = 0,
    eventTime = now,
    downTime = now,
    receivedUptime = now,
    wallTime = wall,
)

private fun gamepads(): dynamic {
    val nav = window.navigator.asDynamic()
    return if (nav.getGamepads != undefined) nav.getGamepads() else js("[]")
}

/** The keyboard and the connected controllers, in the shape Android reports its input devices. */
private fun devices(): List<DeviceInfo> {
    val list = mutableListOf(device(KEYBOARD_ID, "Keyboard", "keyboard", Devices.SOURCE_KEYBOARD, Devices.KEYBOARD_TYPE_ALPHABETIC))
    val pads = gamepads()
    for (i in 0 until pads.length as Int) {
        val pad = pads[i] ?: continue
        if (!(pad.connected as Boolean)) continue
        val name = pad.id as String
        list += device(GAMEPAD_ID_BASE + (pad.index as Int), name, "gamepad:$name", Devices.SOURCE_GAMEPAD or Devices.SOURCE_JOYSTICK, Devices.KEYBOARD_TYPE_NON_ALPHABETIC)
    }
    return list
}

private fun device(id: Int, name: String, descriptor: String, sources: Int, keyboardType: Int) = DeviceInfo(
    id = id, name = name, descriptor = descriptor, vendorId = 0, productId = 0, sources = sources, isVirtual = false,
    isExternal = id != KEYBOARD_ID, keyboardType = keyboardType, controllerNumber = 0, isEnabled = true,
    layoutKeys = listOf("DPAD_UP", "DPAD_DOWN", "DPAD_LEFT", "DPAD_RIGHT", "ENTER"), motionAxes = 0,
)

private fun refreshDevices() {
    val list = devices()
    diag.setDevices(list)
    match.onDevicesChanged(list)
}

private fun uptime(): Long = nanoTime() / 1_000_000

// ------------------------------------------------------------------ settings, report, offline

private const val PREFIX = "tank."

private fun pref(key: String): String? = runCatching { localStorage.getItem(PREFIX + key) }.getOrNull()

private fun loadSettings() {
    match.applySettings(
        mode = runCatching { MoveMode.valueOf(pref("move_mode") ?: "") }.getOrDefault(MoveMode.CONTINUOUS),
        smoothingMs = pref("smoothing_ms")?.toLongOrNull() ?: 0L,
        music = pref("music_volume")?.toIntOrNull() ?: 75,
        sfx = pref("sfx_volume")?.toIntOrNull() ?: 75,
        diagnostics = pref("diagnostics") == "true",
        hi = pref("hi_score")?.toIntOrNull() ?: TankMatch.DEFAULT_HI_SCORE,
        difficulty = runCatching { Difficulty.valueOf(pref("difficulty") ?: "") }.getOrDefault(Difficulty.NORMAL),
    )
}

private fun saveSettings() {
    runCatching {
        mapOf(
            "move_mode" to match.moveMode.name,
            "difficulty" to match.difficulty.name,
            "smoothing_ms" to match.input.smoothingMs.toString(),
            "music_volume" to match.musicVolume.toString(),
            "sfx_volume" to match.sfxVolume.toString(),
            "diagnostics" to match.showDiagnostics.toString(),
            "hi_score" to match.hiScore.toString(),
        ).forEach { (k, v) -> localStorage.setItem(PREFIX + k, v) }
    }
}

/** The session report, handed to the browser as a download (on iPhone and iPad: the share and save sheet). */
private fun saveReport(): String? = runCatching {
    val report = diag.buildReport("TANKS BATTLE RETRO – SESSION REPORT", match.reportSections())
    val name = "tanks-battle-retro-report-${currentTimeMillis()}.txt"
    val url = URL.createObjectURL(Blob(arrayOf(report), BlobPropertyBag(type = "text/plain;charset=utf-8")))
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url
    a.download = name
    document.body?.appendChild(a)
    a.click()
    a.remove()
    window.setTimeout({ URL.revokeObjectURL(url) }, 10_000)
    "Downloads/$name"
}.getOrNull()

private fun environment(): List<Pair<String, String>> {
    val nav = window.navigator
    return listOf(
        "Platform" to if (isIosApp()) "iPhone/iPad app (web view)" else "Web browser",
        "User agent" to nav.userAgent,
        "Screen" to "${window.screen.width}×${window.screen.height} CSS px, devicePixelRatio ${window.devicePixelRatio}",
        "Touch" to touch.toString(),
        "Standalone (Home Screen)" to isStandalone().toString(),
    )
}

/** True inside the App Store app (ios/), whose web view sets this flag before the page loads. */
private fun isIosApp(): Boolean = js("window.TANKS_IOS_APP === true") as Boolean

private fun isStandalone(): Boolean =
    js("(window.navigator.standalone === true) || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)") as Boolean

/** Caches the game for offline play once it has loaded (served over https, or from localhost). */
private fun registerServiceWorker() {
    val sw = window.navigator.asDynamic().serviceWorker ?: return
    runCatching { sw.register("sw.js") }
}
